/**
 * Tiny pub/sub for the "quota reached" intelligent modal.
 *
 * The dialog component subscribes once at mount; any caller that catches a
 * quota-exceeded backend error dispatches the event to open the role-aware
 * dialog instead of showing a stress-inducing toast.
 *
 * Detection rule (kept narrow on purpose — only the analysis quota, NOT
 * other 403s like permissions, NOT user-limit, NOT trial-only screens):
 *   - HTTP status 403 AND
 *   - detail string contains "Quota d'analyse atteint" OR "essai gratuit"
 */
const EVENT_NAME = 'quota:analysis-reached';

export function isAnalysisQuotaError(error) {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status !== 403 || typeof detail !== 'string') return false;
    return /quota d'analyse atteint/i.test(detail) || /essai gratuit/i.test(detail);
}

export function openQuotaReachedDialog(detail = '') {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
}

export function subscribeQuotaReached(handler) {
    if (typeof window === 'undefined') return () => {};
    const listener = (e) => handler(e?.detail || '');
    window.addEventListener(EVENT_NAME, listener);
    return () => window.removeEventListener(EVENT_NAME, listener);
}

/**
 * Convenience wrapper for try/catch in API consumers:
 *   handleAnalyzeError(error, t) → opens the modal if quota, else shows toast.
 * Returns true if the error was consumed (quota), false otherwise.
 */
export function handleAnalyzeError(error, fallbackToast) {
    if (isAnalysisQuotaError(error)) {
        openQuotaReachedDialog(error?.response?.data?.detail || '');
        return true;
    }
    if (typeof fallbackToast === 'function') fallbackToast();
    return false;
}
